import React, { useState, useEffect } from 'react';
import { path } from 'ramda';
import { guid } from '../../common/ambient';
import classNames from 'classnames';
import AlertSet from './AlertSet';
import {
  useFetchAlertSets,
  useCreateAlertSet,
  useUpdateAlertSet,
  useRemoveAlertSet, 
  useAlertSetSync
} from './redux/hooks';
import { useDoModal, useUpdateSetting, useUpdateAlertSetGroupMembers } from '../user/redux/hooks';
import { useRefreshUser } from '../user/redux/hooks';
import { AlertSetMemberCard, alertSetOk } from '.';
import { isPlus, subIsTeams } from '../payment';
// import PropTypes from 'prop-types';

const MAX_MEMBERS = 5
const MAX_ALERT_SETS = 8

export default function AlertSets() {
  const { fetchAlertSets, alertSets: _alertSets } = useFetchAlertSets();
  const { createAlertSet } = useCreateAlertSet();
  const { updateAlertSet } = useUpdateAlertSet();
  const { removeAlertSet } = useRemoveAlertSet();
  const { refreshUser } = useRefreshUser();
  const { doModal } = useDoModal();
  const { userChecked, info, updateSetting } = useUpdateSetting();
  const { updateAlertSetGroupMembers } = useUpdateAlertSetGroupMembers();
  const [alertSets, setAlertSets] = useState([]);
  const [editingAlertSet, setEditingAlertSet] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);

  useAlertSetSync({
    alertSets,
    groupMembers,
    userId: path(['_id'], info)
  });
  // Refresh user data when component mounts to ensure we have the latest verification status
  useEffect(() => {
    if (userChecked && info._id) {
      refreshUser();
    }
  }, [refreshUser, userChecked, info._id]);

  useEffect(() => {
    try {
      if (userChecked && info._id) {
        fetchAlertSets({
          userId: info._id
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [fetchAlertSets, userChecked, info._id]);
  useEffect(() => {
    setAlertSets(_alertSets);
  }, [_alertSets, setAlertSets]);
  useEffect(() => {
    if (userChecked && info) {
      // After a successful email verification, the user info should be refreshed
      // to get the latest verification status

      const gm = (info.alertSetGroupMembers || [{
        _id: info._id,
        name: 'Group Admin (You)',
        email: info.email,
        phone: info.phone,
        alertPrefs: [],
        admin: true
      }])
        // make sure the admin always has updated email and phone
        // and preserve emailVerified status from member data
        .map(m => {
          if (m._id === info._id) {
            return {
              ...m,
              email: info.email,
              phone: info.phone,
              admin: true
            }
          }
          // For non-admin members, preserve their existing properties but update
          // from the server response if available
          return { 
            ...m,
            // We preserve emailVerified status if it exists
            emailVerified: m.emailVerified !== undefined ? m.emailVerified : false,
            phoneVerified: m.phoneVerified !== undefined ? m.phoneVerified : false
          }
        })
      
      setGroupMembers(gm)
    }
  }, [userChecked, info]);


  const membersLeft = MAX_MEMBERS - groupMembers.length;

  if (!userChecked) {
    return <div>Loading...</div>;
  } else if (!info || !info._id) {
    return <div>Please log in to manage alert sets.</div>;
  } else if (!isPlus(info) || (isPlus(info) && !subIsTeams(isPlus(info)))) {
    return (
      <div className="alert-alert-sets">
        <div className='block'>
          <h3>Team Alerts</h3>
          <p>You must be a AWN+ Teams subscriber to access this feature.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="alert-alert-sets">
      <div className='block'>
        <h3>Team Alerts</h3>
        <p>This is the team you manage. Here, you can create and assign alert sets, add or remove members, and adjust their settings.</p>
      </div>
      <div className='block'>
        <h3>Manage team members</h3>
        <p>{membersLeft < MAX_MEMBERS ? `You can add up to ${membersLeft} more members.` : ''  }</p>
        {membersLeft > 0 && (
          <>
            <h4>Invite new team member</h4>
            <div className="invitation-buttons">
              <a
                className='btn btn-primary'
                onClick={() => {
                  setGroupMembers([...groupMembers, { 
                    _id: guid(),
                    name: '', 
                    email: '', 
                    phone: '', 
                    alertPrefs: [], 
                    admin: false,
                    isNew: true, // Flag to indicate this is a new member
                    inviteMethod: 'email' // Flag to indicate this is an email invitation
                  }]);
                  setEditingMember(groupMembers.length);
                }}
              >Invite via Email</a>
              <a
                className='btn btn-primary'
                onClick={() => {
                  setGroupMembers([...groupMembers, { 
                    _id: guid(),
                    name: '', 
                    email: '', 
                    phone: '', 
                    alertPrefs: [], 
                    admin: false,
                    isNew: true, // Flag to indicate this is a new member
                    inviteMethod: 'sms' // Flag to indicate this is an SMS invitation
                  }]);
                  setEditingMember(groupMembers.length);
                }}
              >Invite via SMS</a>
            </div>
          </>
        )}
        <h4>Members list</h4>
        <div className='group-members'>
          {groupMembers.map((member, i) => (
            <div className={classNames('group-member-wrap', `i-${i}`)} key={member._id}>
              <AlertSetMemberCard
                editing={editingMember === i}
                member={member} 
                alertSets={alertSets}
                onCancel={() => {
                  // Only remove the member if it's a new member that has never been saved
                  // (has no email or phone) and was created in this edit session
                  if (!member.email && !member.phone && editingMember === i && member.isNew) {
                    setGroupMembers(groupMembers.filter((_, j) => i !== j));
                  } else {
                    // Otherwise, just exit edit mode without removing
                    setEditingMember(null);
                  }
                }}
                onChange={newMember => {
                  // Set isNew to false when the member is saved
                  if (newMember.isNew) {
                    newMember.isNew = false;
                  }

                  // Ensure verification status is preserved

                  const newMembers = groupMembers.map((m, j) => {
                    if (i === j) {
                      // For the member we're updating, ensure we preserve the verification status
                      return {
                        ...newMember,
                        emailVerified: newMember.emailVerified !== undefined ? newMember.emailVerified : m.emailVerified,
                        phoneVerified: newMember.phoneVerified !== undefined ? newMember.phoneVerified : m.phoneVerified
                      };
                    }
                    return m;
                  });

                  updateAlertSetGroupMembers(newMembers);
                  setEditingMember(null)
                }}
              />
              {/* If not editing and member has no alert sets and has valid verified contact method, show "Assign Alert Sets" button */}
              {editingMember !== i && 
               (!member.alertSets || member.alertSets.length === 0) && 
               ((member.email && member.emailVerified) 
                || (member.phone && member.phoneVerified)
                || member.admin
              ) && (
                <div className="assign-alert-sets-container">
                  <a 
                    className="btn btn-primary assign-alert-sets-btn"
                    onClick={() => setEditingMember(i)}
                  >
                    Assign Alert Sets
                  </a>
                </div>
              )}
              <div className='buttons-wrap'>
                <div className='buttons-rounded'>
                  <a
                    onClick={() => setEditingMember(i === editingMember ? null : i)}
                    className='trash glyphicon glyphicon-pencil'
                    title='Edit Member'
                  />
                  <a
                    onClick={() => {
                      if (window.confirm('Are you sure you want to remove this member?')) {
                        updateAlertSetGroupMembers(groupMembers.filter((_, j) => i !== j));
                      }
                    }}
                    className='trash glyphicon glyphicon-trash'
                    title='Delete Member'
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className='block'>
        <h3>Alert Sets</h3>
        <p>Create and customize up to {MAX_ALERT_SETS} alert sets for your team.</p>
        {alertSets.length < MAX_ALERT_SETS && (
          <button
            onClick={() => {
              doModal({
                type: 'create-alert-set',
                data: {
                  onCreate: newAlertSet => {
                    doModal(null);
                    const newAlertSets = [...alertSets, newAlertSet];
                    setAlertSets(newAlertSets);
                    setEditingAlertSet(newAlertSets.length - 1);
                  }
                }
              })
            }}
            className='btn btn-primary'
          >Create Alert Set</button>
        )}
        <div className='alert-sets'>
          {alertSets && alertSets.map((alertSet, i) => (
            <div className='alert-set-wrap' key={i}>
              <AlertSet 
                alertSet={alertSet} 
                editing={editingAlertSet === i} 
                onChange={async newAlertSet => {
                  if (newAlertSet._id) {
                    await updateAlertSet(newAlertSet._id, newAlertSet, false);
                  } else {
                    await createAlertSet(newAlertSet);
                    // Refresh alert sets to get the latest data with proper IDs
                    fetchAlertSets({
                      userId: info._id
                    });
                  }
                  setEditingAlertSet(null)
                }}
                onCancel={() => {
                  if (!alertSetOk(alertSet)) {
                    setAlertSets(alertSets.filter((_, j) => i !== j));
                  }
                  setEditingAlertSet(null)
                }}
              />
              <div className='buttons-wrap'>
                <div className='buttons-rounded'>
                  <a
                    onClick={() => setEditingAlertSet(i)}
                    className='trash glyphicon glyphicon-pencil'
                    title='Edit Alert Set'
                  />
                  <a
                    onClick={async () => {
                      // Check if we've hit the max limit
                      if (alertSets.length >= MAX_ALERT_SETS) {
                        alert(`You've reached the maximum of ${MAX_ALERT_SETS} alert sets. Please delete an existing alert set before duplicating.`);
                        return;
                      }

                      // Create a copy without the _id so it's treated as new
                      const duplicatedAlertSet = { ...alertSet };
                      delete duplicatedAlertSet._id;

                      // Add "(copy)" to the title
                      duplicatedAlertSet.title = `${alertSet.title} (copy)`;

                      // Save the duplicate to backend
                      await createAlertSet(duplicatedAlertSet);

                      // Refresh alert sets to get the latest data with proper IDs
                      fetchAlertSets({
                        userId: info._id
                      });
                    }}
                    className='trash glyphicon glyphicon-duplicate'
                    title='Duplicate Alert Set'
                  />
                  <a
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this alert set?')) {
                        setAlertSets(alertSets.filter((_, j) => i !== j));
                        await removeAlertSet(alertSet._id);
                        refreshUser();
                      }
                    }}
                    className='trash glyphicon glyphicon-trash'
                    title='Delete Alert Set'
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


AlertSets.propTypes = {};
AlertSets.defaultProps = {};

AlertSets.displayName = 'AlertSets'
